import {
  Card,
  CardContent,
  Fade,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Input from "../../../components/ui/Input";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useIntl } from "../../../contexts/IntlContext";
import Select from "../../../components/ui/Select";
import StatusPill, { StatusPillLevel } from "../../../components/ui/StatusPill";
import { AuthContext } from "../../../contexts/AuthContext";
import { hasOperationalAccess } from "../../../utils/authRole";
import {
  getLiveStatusLevel,
  isUpcomingLiveStatus,
  formatLiveStatus as formatLiveStatusUtil,
} from "../../../utils/liveStatus";
import api from "../../../services/api";
import { TableEmptyState } from "../../../components/table/TableEmptyState";
import { TableSkeletonRows } from "../../../components/table/TableSkeletonRows";
import { StandardTablePagination } from "../../../components/table/StandardTablePagination";
import FiltersBar from "../../../components/ui/FiltersBar";
import { LIVE_STATUS_POLL_MS, useVisiblePolling } from "../../../hooks/useVisiblePolling";
import { fontWeights } from "../../../themes/fontWeights";

type StartedLiveRow = {
  liveId: number;
  name: string;
  youtubeId?: string;
  deviceName?: string;
  placeName?: string;
  status?: string;
  minDate?: string;
  startDate?: string;
  endDate?: string;
};

type PrivateLiveRow = {
  liveId: number;
  name: string;
  youtubeId?: string;
  deviceName?: string;
  placeName?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  paymentPending?: boolean;
  paymentStatus?: string;
  billedValue?: number;
  mpPreference?: string;
  readOnlyFromStarted?: boolean;
};

type PrivateLivesPaginatedResponse = {
  data?: PrivateLiveRow[];
  count?: number;
  page?: number;
  limit?: number;
  filters?: {
    establishments?: string[];
    devices?: string[];
  };
};

const parseIsoToTs = (value?: string) => {
  if (!value) return null;
  const normalized = value.replace(/([+-]\d{2})(\d{2})$/, "$1:$2");
  const ts = Date.parse(normalized);
  return Number.isFinite(ts) ? ts : null;
};

const normalizeToMinuteKey = (value?: string | null) => String(value ?? "").slice(0, 16);
const normalizeStatusKey = (value?: string | null) => String(value ?? "").trim().toUpperCase();
const normalizePaymentStatusKey = (value?: string | null) => String(value ?? "").trim().toUpperCase();
const buildScheduleMatchKey = (startDate?: string, endDate?: string, status?: string | null) =>
  `${normalizeToMinuteKey(startDate)}|${normalizeToMinuteKey(endDate)}|${normalizeStatusKey(status)}`;
const buildPrivateRowKey = (row: PrivateLiveRow) =>
  `${Number(row.liveId)}|${buildScheduleMatchKey(row.startDate, row.endDate, row.status)}`;

const getLiveRowSortTs = (row: { startDate?: string; endDate?: string; liveId?: number }) => {
  const startTs = parseIsoToTs(row.startDate);
  const endTs = parseIsoToTs(row.endDate);
  if (startTs != null || endTs != null) return Math.max(startTs ?? 0, endTs ?? 0);
  return Number(row.liveId ?? 0) || 0;
};

const normalizeKeyLoose = (value: string) => {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9]/g, "");
};

const matchesEstablishmentKey = (establishmentKey: string, placeName: string | null | undefined) => {
  if (!establishmentKey) return true;
  const placeLoose = normalizeKeyLoose(String(placeName ?? ""));
  // If place is missing, don't hide the row (some downloader payloads omit placeName).
  if (!placeLoose) return true;
  if (placeLoose === establishmentKey) return true;
  return placeLoose.includes(establishmentKey) || establishmentKey.includes(placeLoose);
};

const getHttpErrorMessage = (intl: ReturnType<typeof useIntl>, error: any) => {
  const data = error?.response?.data;
  const candidates = [
    typeof data === "string" ? data : "",
    data && typeof data.message === "string" ? data.message : "",
    data && typeof data.error === "string" ? data.error : "",
    typeof error?.message === "string" ? error.message : "",
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  const normalizeLoose = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  if (candidates.some((value) => normalizeLoose(value).includes("periodo escolhido ja possui agendamentos"))) {
    return intl.formatMessage({ id: "liveStreams.scheduleConflictPeriod" });
  }
  if (candidates.some((value) => normalizeLoose(value).includes("invalid or expired token"))) {
    return intl.formatMessage({ id: "messages.youtubeTokenExpired" });
  }

  if (candidates.length > 0) return candidates[0];
  return intl.formatMessage({ id: "global.generic_error" });
};

export default function LivesManager({
  establishmentName,
  focusLiveId,
}: {
  establishmentName?: string;
  focusLiveId?: number | null;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { authInfo } = useContext(AuthContext);
  const canOperate = hasOperationalAccess(authInfo?.role);

  const formatLiveStatus = useCallback(
    (status?: string | null) => formatLiveStatusUtil(intl, status),
    [intl]
  );

  const [viewTab, setViewTab] = useState<"upcoming" | "past">("upcoming");

  const formatShortDateTime = useCallback(
    (value?: string) => {
      const ts = parseIsoToTs(value);
      if (ts == null) return "";
      const date = new Date(ts);
      try {
        return new Intl.DateTimeFormat(intl.locale, {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }).format(date);
      } catch {
        return date.toLocaleString();
      }
    },
    [intl.locale]
  );

  const formatPaymentStatus = useCallback(
    (paymentStatus?: string | null, paid?: boolean | null) => {
      const normalized = normalizePaymentStatusKey(paymentStatus);
      if (normalized === "DIRECT_FINANCE") {
        return intl.formatMessage({ id: "liveStreams.paymentStatusDirectFinance" });
      }
      if (normalized === "AWAITING_SCHEDULES") {
        return intl.formatMessage({ id: "liveStreams.paymentStatusAwaitingSchedules" });
      }
      if (normalized === "PAID") return intl.formatMessage({ id: "liveStreams.paymentStatusPaid" });
      if (normalized === "PENDING") return intl.formatMessage({ id: "liveStreams.paymentStatusPending" });
      if (paid === true) return intl.formatMessage({ id: "liveStreams.paymentStatusPaid" });
      if (paid === false) return intl.formatMessage({ id: "liveStreams.paymentStatusPending" });
      return intl.formatMessage({ id: "liveStreams.paymentStatusUnknown" });
    },
    [intl]
  );

  const getPaymentStatusLevel = useCallback(
    (paymentStatus?: string | null, paid?: boolean | null): StatusPillLevel => {
      const normalized = normalizePaymentStatusKey(paymentStatus);
      if (normalized === "PAID" || paid === true) return "success";
      if (normalized === "AWAITING_SCHEDULES") return "neutral";
      if (normalized === "PENDING" || paid === false) return "warning";
      return "neutral";
    },
    []
  );

  const [startedLives, setStartedLives] = useState<StartedLiveRow[]>([]);
  const [loadingStarted, setLoadingStarted] = useState(false);
  const [privateLives, setPrivateLives] = useState<PrivateLiveRow[]>([]);
  const [loadingPrivates, setLoadingPrivates] = useState(false);
  const [selectedLiveId, setSelectedLiveId] = useState<number | null>(null);
  const [selectedPrivateRowKey, setSelectedPrivateRowKey] = useState<string | null>(null);
  // Kept in sync via effects below; read inside loadPrivates without being a reactive dependency,
  // so clicking a row (which only changes the selection) doesn't retrigger a reload of the table.
  const selectedLiveIdRef = useRef(selectedLiveId);
  const selectedPrivateRowKeyRef = useRef(selectedPrivateRowKey);
  useEffect(() => {
    selectedLiveIdRef.current = selectedLiveId;
  }, [selectedLiveId]);
  useEffect(() => {
    selectedPrivateRowKeyRef.current = selectedPrivateRowKey;
  }, [selectedPrivateRowKey]);
  const [filterText, setFilterText] = useState("");
  const [filterDeviceName, setFilterDeviceName] = useState<string>("");
  const [filterEstablishmentName, setFilterEstablishmentName] = useState<string>("");
  const [filterHistoryStatus, setFilterHistoryStatus] = useState<string>("");
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [historyEstablishmentOptions, setHistoryEstablishmentOptions] = useState<string[]>([]);
  const [historyDeviceOptions, setHistoryDeviceOptions] = useState<string[]>([]);

  useEffect(() => {
    if (typeof focusLiveId !== "number" || !Number.isFinite(focusLiveId) || focusLiveId <= 0) return;
    setSelectedLiveId(focusLiveId);
  }, [focusLiveId]);

  useEffect(() => {
    if (canOperate) return;
    // When establishment changes, reset selection & filters to avoid showing an empty list while detail is from another place.
    setFilterDeviceName("");
    setFilterText("");
    setSelectedLiveId(null);
  }, [establishmentName, canOperate]);

  const establishmentKey = useMemo(
    () => (establishmentName ? normalizeKeyLoose(String(establishmentName)) : ""),
    [establishmentName]
  );
  const establishmentFilterKey = useMemo(
    () => (filterEstablishmentName ? normalizeKeyLoose(String(filterEstablishmentName)) : ""),
    [filterEstablishmentName]
  );
  const effectiveEstablishmentKey = useMemo(
    () => (canOperate ? establishmentFilterKey : establishmentKey),
    [establishmentFilterKey, establishmentKey, canOperate]
  );

  const historyLives = useMemo(() => privateLives.slice(), [privateLives]);

  const deviceOptions = useMemo(
    () => historyDeviceOptions.slice(),
    [historyDeviceOptions]
  );

  const establishmentOptions = useMemo(
    () => historyEstablishmentOptions.slice(),
    [historyEstablishmentOptions]
  );

  const filteredPrivateLives = useMemo(() => historyLives.slice(), [historyLives]);

  useEffect(() => {
    setHistoryPage(0);
  }, [effectiveEstablishmentKey, filterDeviceName, filterEstablishmentName, filterHistoryStatus, filterText]);

  const historyMaxPage = useMemo(
    () => Math.max(0, Math.ceil(historyTotalCount / historyRowsPerPage) - 1),
    [historyTotalCount, historyRowsPerPage]
  );

  useEffect(() => {
    setHistoryPage((prev) => Math.min(Math.max(prev, 0), historyMaxPage));
  }, [historyMaxPage]);

  const pagedPrivateLives = useMemo(() => filteredPrivateLives.slice(), [filteredPrivateLives]);

  const filteredStartedLives = useMemo(() => {
    const normalizedFilter = filterText.trim().toLowerCase();
    const filtered = startedLives.filter((l) => {
      if (!matchesEstablishmentKey(effectiveEstablishmentKey, l.placeName)) return false;
      if (filterDeviceName && (l.deviceName ?? "") !== filterDeviceName) return false;
      if (!normalizedFilter) return true;
      const haystack = [l.name ?? "", l.placeName ?? "", l.deviceName ?? "", l.status ?? "", String(l.liveId)]
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedFilter);
    });
    filtered.sort((a, b) => getLiveRowSortTs(b) - getLiveRowSortTs(a));
    return filtered;
  }, [effectiveEstablishmentKey, filterDeviceName, filterText, startedLives]);

  // Rows pinned at the top of the merged table (currently live, with next status change), independent of history pagination.
  // Same pinning/order for every role: filteredStartedLives is already scoped to the viewer's
  // establishment for non-operational users (see effectiveEstablishmentKey above).
  const pinnedRows = filteredStartedLives;
  const pinnedLiveIds = useMemo(() => new Set(pinnedRows.map((l) => l.liveId)), [pinnedRows]);
  const columnsCount = canOperate ? 6 : 5;

  // Exclude rows already shown pinned at the top to avoid showing the same live twice.
  const visiblePrivateLives = useMemo(
    () => pagedPrivateLives.filter((l) => !pinnedLiveIds.has(l.liveId)),
    [pagedPrivateLives, pinnedLiveIds]
  );

  // Divide as linhas já carregadas nesta página entre "próximas" (ao vivo agora + agendadas) e
  // "anteriores" (finalizadas/falhas/canceladas), só para exibição — não afeta busca/paginação.
  const upcomingPrivateLives = useMemo(
    () => visiblePrivateLives.filter((l) => isUpcomingLiveStatus(l.status)),
    [visiblePrivateLives]
  );
  const pastPrivateLives = useMemo(
    () => visiblePrivateLives.filter((l) => !isUpcomingLiveStatus(l.status)),
    [visiblePrivateLives]
  );

  // `silent` = atualização em segundo plano (polling): não troca a tabela por skeleton.
  const startedSeqRef = useRef(0);
  const startedInFlightRef = useRef(0);
  const loadStarted = useCallback(async (silent = false) => {
    if (silent && startedInFlightRef.current > 0) return;
    const seq = silent ? startedSeqRef.current : ++startedSeqRef.current;
    startedInFlightRef.current += 1;
    if (!silent) setLoadingStarted(true);
    try {
      const response = await api.get<{ data: StartedLiveRow[] }>("/app/lives/started", {
        params: {
          page: 0,
          limit: 25,
          _: Date.now(),
        },
        headers: { "Cache-Control": "no-cache" },
      });
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      if (seq === startedSeqRef.current) setStartedLives(rows);
    } finally {
      startedInFlightRef.current -= 1;
      if (!silent && seq === startedSeqRef.current) setLoadingStarted(false);
    }
  }, []);

  const privatesSeqRef = useRef(0);
  const privatesInFlightRef = useRef(0);
  const loadPrivates = useCallback(async (silent = false) => {
    if (silent && privatesInFlightRef.current > 0) return;
    // Uma carga nova (filtro/página) invalida respostas anteriores ainda em voo.
    const seq = silent ? privatesSeqRef.current : ++privatesSeqRef.current;
    privatesInFlightRef.current += 1;
    if (!silent) setLoadingPrivates(true);
    try {
      const response = await api.get<PrivateLivesPaginatedResponse>("/app/lives/privates/paginated", {
        params: {
          page: historyPage,
          limit: historyRowsPerPage,
          placeName: canOperate ? (filterEstablishmentName || undefined) : (establishmentName || undefined),
          deviceName: filterDeviceName || undefined,
          historyStatus: filterHistoryStatus || undefined,
          search: filterText.trim() || undefined,
          _: Date.now(),
        },
        headers: { "Cache-Control": "no-cache" },
      });
      if (seq !== privatesSeqRef.current) return;
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      const total = Number(response.data?.count ?? 0);
      const establishments = Array.isArray(response.data?.filters?.establishments)
        ? response.data!.filters!.establishments!
        : [];
      const devices = Array.isArray(response.data?.filters?.devices)
        ? response.data!.filters!.devices!
        : [];

      setPrivateLives(rows);
      setHistoryTotalCount(Number.isFinite(total) ? total : 0);
      setHistoryEstablishmentOptions(establishments);
      setHistoryDeviceOptions(devices);

      // Em segundo plano não mexe na seleção do usuário.
      if (silent) return;
      const selectedPrivateRowKey = selectedPrivateRowKeyRef.current;
      const selectedLiveId = selectedLiveIdRef.current;

      if (selectedPrivateRowKey && rows.some((r) => buildPrivateRowKey(r) === selectedPrivateRowKey)) return;

      if (selectedLiveId && rows.some((r) => r.liveId === selectedLiveId)) {
        const firstForLive = rows.find((r) => r.liveId === selectedLiveId) ?? null;
        if (firstForLive) setSelectedPrivateRowKey(buildPrivateRowKey(firstForLive));
        return;
      }

      const firstRow = rows[0] ?? null;
      if (firstRow?.liveId) {
        setSelectedLiveId(firstRow.liveId);
        setSelectedPrivateRowKey(buildPrivateRowKey(firstRow));
      } else {
        setSelectedLiveId(null);
        setSelectedPrivateRowKey(null);
      }
    } finally {
      privatesInFlightRef.current -= 1;
      if (!silent && seq === privatesSeqRef.current) setLoadingPrivates(false);
    }
  }, [
    establishmentName,
    filterDeviceName,
    filterEstablishmentName,
    filterHistoryStatus,
    filterText,
    historyPage,
    historyRowsPerPage,
    canOperate,
  ]);

  useEffect(() => {
    let canceled = false;
    const load = async () => {
      try {
        await loadStarted();
      } catch (err: any) {
        if (canceled) return;
        toast.error(getHttpErrorMessage(intl, err));
      }
    };
    void load();
    return () => {
      canceled = true;
    };
  }, [intl, loadStarted]);

  useEffect(() => {
    let canceled = false;
    const load = async () => {
      try {
        await loadPrivates();
      } catch (err: any) {
        if (canceled) return;
        toast.error(getHttpErrorMessage(intl, err));
      }
    };
    void load();
    return () => {
      canceled = true;
    };
  }, [intl, loadPrivates]);

  // O status (WAIT_START -> STARTED -> FINISHED) muda no backend sem ação do usuário; sem isso a
  // tela só refletia a mudança após recarregar a página.
  useVisiblePolling(async () => {
    await Promise.all([loadStarted(true), loadPrivates(true)]);
  }, LIVE_STATUS_POLL_MS);

  const activeFilterCount = [
    filterText,
    filterEstablishmentName,
    filterDeviceName,
    filterHistoryStatus,
  ].filter(Boolean).length;

  return (
    <Stack spacing={2}>
      <FiltersBar activeFilterCount={activeFilterCount} sticky={false}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Input
            label={intl.formatMessage({ id: "liveStreams.filterDescription" })}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            sx={{ width: { xs: "100%", sm: 220 } }}
          />
          {canOperate ? (
            <Select
              label={intl.formatMessage({ id: "liveStreams.filterEstablishment" })}
              value={filterEstablishmentName}
              onChange={(e) => setFilterEstablishmentName(String(e.target.value))}
              sx={{ width: { xs: "100%", sm: 220 } }}
            >
              <MenuItem value="">{intl.formatMessage({ id: "liveStreams.filterAll" })}</MenuItem>
              {establishmentOptions.map((place) => (
                <MenuItem key={place} value={place}>
                  {place}
                </MenuItem>
              ))}
            </Select>
          ) : null}
          <Select
            label={intl.formatMessage({ id: "liveStreams.filterDevice" })}
            value={filterDeviceName}
            onChange={(e) => setFilterDeviceName(String(e.target.value))}
            sx={{ width: { xs: "100%", sm: 220 } }}
          >
            <MenuItem value="">{intl.formatMessage({ id: "liveStreams.filterAll" })}</MenuItem>
            {deviceOptions.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </Select>
          {canOperate ? (
            <Select
              label={intl.formatMessage({ id: "liveStreams.filterHistoryStatus" })}
              value={filterHistoryStatus}
              onChange={(e) => setFilterHistoryStatus(String(e.target.value))}
              sx={{ width: { xs: "100%", sm: 220 } }}
            >
              <MenuItem value="">{intl.formatMessage({ id: "liveStreams.filterAll" })}</MenuItem>
              <MenuItem value="WAIT_START">{intl.formatMessage({ id: "liveStreams.historyStatusWaiting" })}</MenuItem>
              <MenuItem value="COMPLETED">{intl.formatMessage({ id: "liveStreams.historyStatusCompleted" })}</MenuItem>
            </Select>
          ) : null}
        </Stack>
      </FiltersBar>

      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {intl.formatMessage({ id: "liveStreams.livesTitle" })}
            </Typography>
            <Tabs
              value={viewTab}
              onChange={(_e, value) => setViewTab(value)}
              sx={{ mb: 1, minHeight: 36 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                value="upcoming"
                label={`${intl.formatMessage({ id: "liveStreams.tabUpcoming" })} (${
                  pinnedRows.length + upcomingPrivateLives.length
                })`}
                sx={{ minHeight: 36, py: 0.5 }}
              />
              <Tab
                value="past"
                label={`${intl.formatMessage({ id: "liveStreams.tabPast" })} (${pastPrivateLives.length})`}
                sx={{ minHeight: 36, py: 0.5 }}
              />
            </Tabs>
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 180 }}>{intl.formatMessage({ id: "liveStreams.name" })}</TableCell>
                    <TableCell sx={{ minWidth: 110 }}>{intl.formatMessage({ id: "liveStreams.startAt" })}</TableCell>
                    <TableCell sx={{ minWidth: 110 }}>{intl.formatMessage({ id: "liveStreams.endAt" })}</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>{intl.formatMessage({ id: "liveStreams.status" })}</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>{intl.formatMessage({ id: "liveStreams.paymentColumn" })}</TableCell>
                    {canOperate ? (
                      <TableCell sx={{ minWidth: 200 }}>{intl.formatMessage({ id: "liveStreams.nextChange" })}</TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <Fade in key={loadingStarted || loadingPrivates ? "loading" : "loaded"} timeout={200}>
                  <TableBody>
                    {(loadingStarted || loadingPrivates) && <TableSkeletonRows rows={5} columns={columnsCount} />}
                    {!loadingStarted &&
                      !loadingPrivates &&
                      viewTab === "upcoming" &&
                      pinnedRows.map((l) => {
                        const rowKey = buildPrivateRowKey(l);
                        const selected = canOperate && rowKey === selectedPrivateRowKey;
                        return (
                          <TableRow
                            key={`started-${rowKey}`}
                            hover={canOperate}
                            selected={selected}
                            onClick={
                              canOperate
                                ? () => {
                                    setSelectedLiveId(l.liveId);
                                    setSelectedPrivateRowKey(rowKey);
                                    navigate(`/transmissoes-ao-vivo/live/${l.liveId}`, {
                                      state: {
                                        preferredScheduleKey: buildScheduleMatchKey(l.startDate, l.endDate, l.status),
                                        initialScheduleRangeFallback: { startDate: l.startDate, endDate: l.endDate },
                                      },
                                    });
                                  }
                                : undefined
                            }
                            sx={{
                              cursor: canOperate ? "pointer" : "default",
                              bgcolor: "action.hover",
                              opacity: canOperate ? 1 : 0.85,
                              borderLeft: "3px solid",
                              borderLeftColor: "success.main",
                            }}
                          >
                            <TableCell>
                              <Typography sx={{ fontWeight: fontWeights.bold }} noWrap>
                                {l.name}
                              </Typography>
                            </TableCell>
                            <TableCell>{l.startDate ? formatShortDateTime(l.startDate) : "-"}</TableCell>
                            <TableCell>{l.endDate ? formatShortDateTime(l.endDate) : "-"}</TableCell>
                            <TableCell>
                              <StatusPill
                                size="small"
                                label={formatLiveStatus(l.status)}
                                level={getLiveStatusLevel(l.status)}
                              />
                            </TableCell>
                            <TableCell>-</TableCell>
                            {canOperate ? <TableCell>{l.minDate ?? "-"}</TableCell> : null}
                          </TableRow>
                        );
                      })}
                    {!loadingPrivates &&
                      (viewTab === "upcoming" ? upcomingPrivateLives : pastPrivateLives).map((l) => {
                        const rowKey = buildPrivateRowKey(l);
                        const selected = rowKey === selectedPrivateRowKey;
                        const isReadOnlyRow = !canOperate && !!l.readOnlyFromStarted;
                        const paidFromRow = typeof l.paymentPending === "boolean" ? !l.paymentPending : null;
                        return (
                          <TableRow
                            key={rowKey}
                            hover={!isReadOnlyRow}
                            selected={selected}
                            onClick={() => {
                              if (isReadOnlyRow) return;
                              setSelectedLiveId(l.liveId);
                              setSelectedPrivateRowKey(rowKey);
                              navigate(`/transmissoes-ao-vivo/live/${l.liveId}`, {
                                state: {
                                  preferredScheduleKey: buildScheduleMatchKey(l.startDate, l.endDate, l.status),
                                  initialScheduleRangeFallback: { startDate: l.startDate, endDate: l.endDate },
                                },
                              });
                            }}
                            sx={{
                              cursor: isReadOnlyRow ? "default" : "pointer",
                              opacity: isReadOnlyRow ? 0.85 : 1,
                            }}
                          >
                            <TableCell>
                              <Typography sx={{ fontWeight: fontWeights.bold }} noWrap>
                                {l.name}
                              </Typography>
                            </TableCell>
                            <TableCell>{l.startDate ? formatShortDateTime(l.startDate) : "-"}</TableCell>
                            <TableCell>{l.endDate ? formatShortDateTime(l.endDate) : "-"}</TableCell>
                            <TableCell>
                              <StatusPill
                                size="small"
                                label={formatLiveStatus(l.status)}
                                level={getLiveStatusLevel(l.status)}
                              />
                            </TableCell>
                            <TableCell>
                              <StatusPill
                                size="small"
                                label={formatPaymentStatus(l.paymentStatus, paidFromRow)}
                                level={getPaymentStatusLevel(l.paymentStatus, paidFromRow)}
                              />
                            </TableCell>
                            {canOperate ? <TableCell>-</TableCell> : null}
                          </TableRow>
                        );
                      })}
                    {!loadingStarted &&
                    !loadingPrivates &&
                    (viewTab === "upcoming"
                      ? !pinnedRows.length && !upcomingPrivateLives.length
                      : !pastPrivateLives.length) ? (
                      <TableEmptyState colSpan={columnsCount}>
                        {intl.formatMessage({ id: "liveStreams.noLives" })}
                      </TableEmptyState>
                    ) : null}
                  </TableBody>
                </Fade>
              </Table>
            </TableContainer>

            <StandardTablePagination
              count={historyTotalCount}
              rowsPerPage={historyRowsPerPage}
              page={historyPage}
              onPageChange={(_, newPage) => setHistoryPage(newPage)}
              onRowsPerPageChange={(e) => {
                setHistoryRowsPerPage(+e.target.value);
                setHistoryPage(0);
              }}
            />
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
}
